import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { getCrmOutboxStatus, retryCrmOutboxNow } from "@/lib/lead-outbox.functions";
import { Button } from "@/components/ui/button";

export const Route = createFileRoute("/admin/integrations-health")({
  head: () => ({
    meta: [
      { title: "Integrations health · Admin" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: AdminIntegrationsHealth,
});

function AdminIntegrationsHealth() {
  const fetchStatus = useServerFn(getCrmOutboxStatus);
  const retry = useServerFn(retryCrmOutboxNow);
  const qc = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ["crm-outbox-status"],
    queryFn: () => fetchStatus(),
    refetchInterval: 60_000,
  });

  const retryMutation = useMutation({
    mutationFn: () => retry(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["crm-outbox-status"] }),
  });

  return (
    <div>
      <h1 className="text-2xl font-black tracking-tight">Integrations health</h1>
      <p className="mt-1 max-w-3xl text-sm text-muted-foreground">
        Delivery status for leads forwarded from this DSCR site to the Simply Approved CRM. Any lead
        that cannot be delivered is queued here and retried — nothing is dropped.
      </p>

      {isLoading ? <p className="mt-6 text-sm text-muted-foreground">Loading status…</p> : null}
      {error ? (
        <p className="mt-6 text-sm text-destructive">
          Could not load status: {(error as Error).message}
        </p>
      ) : null}

      {data ? (
        <>
          <div className="mt-6 grid gap-4 sm:grid-cols-4">
            <StatCard
              label="CRM endpoint"
              value={data.configured ? "Configured" : "Not configured"}
              hint={data.endpointHost ?? "Set CRM credentials to enable delivery"}
            />
            <StatCard label="Queued" value={String(data.counts.pending)} hint="Awaiting delivery" />
            <StatCard label="Failed" value={String(data.counts.failed)} hint="Retryable" />
            <StatCard label="Delivered" value={String(data.counts.delivered)} hint="Recent window" />
          </div>

          <div className="mt-6">
            <Button
              onClick={() => retryMutation.mutate()}
              disabled={retryMutation.isPending}
            >
              {retryMutation.isPending ? "Retrying…" : "Retry queued deliveries"}
            </Button>
            {retryMutation.data ? (
              <span className="ml-3 text-sm text-muted-foreground">
                {retryMutation.data.delivered} delivered · {retryMutation.data.failed} still failing
              </span>
            ) : null}
          </div>

          <div className="mt-8 overflow-x-auto rounded-2xl border border-border">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-4 py-3">Created</th>
                  <th className="px-4 py-3">Product</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Attempts</th>
                  <th className="px-4 py-3">Last error</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {data.recent.length === 0 ? (
                  <tr>
                    <td className="px-4 py-4 text-muted-foreground" colSpan={5}>
                      No queued deliveries — every lead has gone straight through.
                    </td>
                  </tr>
                ) : (
                  data.recent.map((r) => (
                    <tr key={r.id}>
                      <td className="px-4 py-3">{new Date(r.createdAt).toLocaleString()}</td>
                      <td className="px-4 py-3 uppercase">{r.productType}</td>
                      <td className="px-4 py-3 font-semibold">{r.status}</td>
                      <td className="px-4 py-3">{r.attempts}</td>
                      <td className="px-4 py-3 text-muted-foreground">{r.lastError ?? "—"}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      ) : null}
    </div>
  );
}

function StatCard({ label, value, hint }: { label: string; value: string; hint: string }) {
  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="mt-1 font-display text-2xl font-bold text-brand-dark">{value}</div>
      <div className="mt-1 text-xs text-muted-foreground">{hint}</div>
    </div>
  );
}
